import type { PbxSettingsTranslateFn } from '../types'
import { text } from '../types'
import type { EditPbxFormState } from '../types/pbxSettings.types'
import {
  ADVANCED_NUMERIC_FIELDS,
  AREA_CODES_NOT_ALLOWED,
  CALL_PARK_TIMEOUT_MAX,
  CALL_PARK_TIMEOUT_MIN,
  CALL_PARK_TIMEOUT_RECOMMENDED,
} from './pbxSettings.constants'

type Validator = (
  state: EditPbxFormState,
  t?: PbxSettingsTranslateFn,
) => string | null

const required = (
  getValue: (s: EditPbxFormState) => string,
  key: string,
  fallback: string,
): Validator => {
  return (state, t) => (getValue(state).trim() ? null : text(t, key, fallback))
}

const ADVANCED_NUMERIC_FIELD_REQUIRED_MESSAGES = {
  maximum_allowed_channels: {
    key: 'pbxSettings.validation.maxChannelsRequired',
    fallback: 'Please make an entry for the Max Channels.',
  },
  maximum_lines_in: {
    key: 'pbxSettings.validation.inboundChannelsRequired',
    fallback: 'Please make an entry for the Inbound Channels.',
  },
  maximum_lines_out: {
    key: 'pbxSettings.validation.perExtensionChannelsRequired',
    fallback: 'Please make an entry for the Per Extension Channels.',
  },
  call_log_retention_period_in_months: {
    key: 'pbxSettings.validation.callHistoryRequired',
    fallback: 'Please make an entry for the Call History.',
  },
} as const

const getNumericFieldRangeMessage = (
  t: PbxSettingsTranslateFn | undefined,
  fieldLabel: string,
  min: number,
  max: number,
) => {
  if (!t) {
    return `${fieldLabel} must be between ${min} and ${max}.`
  }
  return text(
    t,
    'pbxSettings.validation.numericFieldRange',
    '{{field}} must be between {{min}} and {{max}}.',
    {
      field: fieldLabel,
      min,
      max,
    },
  )
}

const advancedNumericFieldValidator = (
  fieldConfig: (typeof ADVANCED_NUMERIC_FIELDS)[number],
): Validator => {
  return (state, t) => {
    const rawValue = state.pbx[fieldConfig.key].trim()
    const requiredMessage =
      ADVANCED_NUMERIC_FIELD_REQUIRED_MESSAGES[fieldConfig.key]

    if (!rawValue) {
      return text(t, requiredMessage.key, requiredMessage.fallback)
    }

    if (!/^\d+$/.test(rawValue)) {
      return getNumericFieldRangeMessage(
        t,
        text(
          t,
          `pbxSettings.fields.advanced.${fieldConfig.key}`,
          fieldConfig.label,
        ),
        fieldConfig.min,
        fieldConfig.max,
      )
    }

    const value = Number.parseInt(rawValue, 10)
    if (value < fieldConfig.min || value > fieldConfig.max) {
      return getNumericFieldRangeMessage(
        t,
        text(
          t,
          `pbxSettings.fields.advanced.${fieldConfig.key}`,
          fieldConfig.label,
        ),
        fieldConfig.min,
        fieldConfig.max,
      )
    }

    return null
  }
}

const VALIDATORS: Validator[] = [
  required(
    (s) => s.pbx.name,
    'pbxSettings.validation.pbxDisplayNameRequired',
    'Please make an entry for the PBX Display Name.',
  ),
  required(
    (s) => s.pbx.company_id,
    'pbxSettings.validation.companyRequired',
    'Please select a Company.',
  ),
  required(
    (s) => s.pbx.company_name,
    'pbxSettings.validation.smsSenderNameRequired',
    'Please make an entry for the SMS Sender Name.',
  ),
  required(
    (s) => s.pbx.server_group_id,
    'pbxSettings.validation.serverGroupRequired',
    'Please select a Server Group.',
  ),
  required(
    (s) => s.pbx.provider_gateway_id,
    'pbxSettings.validation.primaryOutboundGatewayRequired',
    'Please select a Primary Outbound Gateway.',
  ),
  required(
    (s) => s.pbx.international_provider_gateway_id,
    'pbxSettings.validation.primaryInternationalGatewayRequired',
    'Please select a Primary International Gateway.',
  ),
  required(
    (s) => s.pbx.emergency_provider_gateway_id,
    'pbxSettings.validation.emergencyGatewayRequired',
    'Please select an Emergency Gateway.',
  ),
  required(
    (s) => s.pbx.industry_type,
    'pbxSettings.validation.industryRequired',
    'Please select an Industry.',
  ),
  ...ADVANCED_NUMERIC_FIELDS.map(advancedNumericFieldValidator),
  required(
    (s) => s.systemSettings.default_time_zone,
    'pbxSettings.validation.defaultTimeZoneRequired',
    'Please select a Default Time Zone.',
  ),

  (state, t) => {
    const value = Number.parseInt(
      state.systemSettings.call_park_timeout.trim(),
      10,
    )
    if (!Number.isFinite(value) || value < CALL_PARK_TIMEOUT_MIN) {
      return text(
        t,
        'pbxSettings.validation.callParkTimeoutRange',
        `Please make an entry for Call Park Timeout between ${CALL_PARK_TIMEOUT_MIN}-${CALL_PARK_TIMEOUT_MAX} (Recommended: ${CALL_PARK_TIMEOUT_RECOMMENDED}).`,
        {
          min: CALL_PARK_TIMEOUT_MIN,
          max: CALL_PARK_TIMEOUT_MAX,
          recommended: CALL_PARK_TIMEOUT_RECOMMENDED,
        },
      )
    }
    if (value > CALL_PARK_TIMEOUT_MAX) {
      return text(
        t,
        'pbxSettings.validation.callParkTimeoutMax',
        `Call Park Timeout cannot exceed ${CALL_PARK_TIMEOUT_MAX} seconds.`,
        { max: CALL_PARK_TIMEOUT_MAX },
      )
    }
    return null
  },

  required(
    (s) => s.systemSettings.extension_digits,
    'pbxSettings.validation.extensionDigitsRequired',
    'Please make an entry for the Extension Digits.',
  ),
  (state, t) => {
    const value = state.systemSettings.extension_digits.trim()
    return ['3', '4', '5'].includes(value)
      ? null
      : text(
          t,
          'pbxSettings.validation.extensionDigitsInvalid',
          'Extension Digits must be 3, 4, or 5.',
        )
  },

  required(
    (s) => s.systemSettings.default_area_code,
    'pbxSettings.validation.defaultAreaCodeRequired',
    'Please make an entry for the Default Area Code.',
  ),
  (state, t) => {
    const code = state.systemSettings.default_area_code.trim()
    if (!/^\d{3}$/.test(code)) {
      return text(
        t,
        'pbxSettings.validation.defaultAreaCodeDigits',
        'Default Area Code must be a 3-digit number.',
      )
    }
    const num = Number.parseInt(code, 10)
    if (num < 200 || num > 998) {
      return text(
        t,
        'pbxSettings.validation.defaultAreaCodeRange',
        'Default Area Code must be between 200 and 998.',
      )
    }
    if (AREA_CODES_NOT_ALLOWED.has(code)) {
      return text(
        t,
        'pbxSettings.validation.defaultAreaCodeDisallowed',
        `The Area Code ${code} is not permitted for the Default Area Code.`,
        { code },
      )
    }
    return null
  },

  required(
    (s) => s.systemSettings.default_caller_id,
    'pbxSettings.validation.defaultCallerIdRequired',
    'Please select a Default Caller ID.',
  ),
  required(
    (s) => s.systemSettings.default_send_fax_number,
    'pbxSettings.validation.defaultFaxNumberRequired',
    'Please select a Default Fax Number.',
  ),
  required(
    (s) => s.systemSettings.default_fax_extension_id,
    'pbxSettings.validation.defaultVirtualFaxRequired',
    'Please select a Default Virtual Fax.',
  ),
  required(
    (s) => s.pbx.preferred_codec,
    'pbxSettings.validation.preferredCodecRequired',
    'Please select a Preferred Codec.',
  ),
]

export const validateEditPbxForm = (
  state: EditPbxFormState,
  t?: PbxSettingsTranslateFn,
): string | null => {
  for (const validate of VALIDATORS) {
    const error = validate(state, t)
    if (error) return error
  }
  return null
}
